// Basic
import { useState } from 'react';
import PropTypes from 'prop-types';
// Store & actions
import actionsBeneficiaries from '@/app/modules/payludd/beneficiaries/api/actionsBeneficiaries';
// Components
import ModalAssociateContracts from '../modals/ModalAssociateContracts';
import Entity from '@/common-utils/components/renders/entity/Entity';
import Spinner from '@/common-utils/components/renders/spinner/Spinner';
import ModalUpdateBeneficiaryLabel from '../modals/ModalUpdateBeneficiaryLabel';
import { Button } from '@/common-utils/components/ui/button';
import ModalCreateBeneficiary from '../modals/ModalCreateBeneficiary';
import ModalDeleteBeneficiary from '../modals/ModalDeleteBeneficiary';
import BeneficiaryAccordion from './BeneficiaryAccordion';
// Common
import ICONS from '@/common-utils/enums/icons';
import { useToast } from '@/common-utils/hooks/use-toast';
// Lang
import { nsBeneficiaries } from '@/app/i18n/namespaces';
import BENEFICIARY_ACTION_TYPES from '../enums/BeneficiaryActionTypes';

/**
 * @param root0
 * @param root0.merchantAccountId
 * @returns {JSX} PayluddBeneficiaries
 */
function PayluddBeneficiaries({ merchantAccountId }) {
    // Toast
    const { toast } = useToast();

    // State
    const [openItems, setOpenItems] = useState([]);
    const [colorPickerBeneficiaryUuid, setColorPickerBeneficiaryUuid] = useState(null);
    const [activeModal, setActiveModal] = useState(null);
    const [showModalCreateBeneficiary, setShowModalCreateBeneficiary] = useState(false);

    // API Calls
    // -- Retrieve beneficiaries
    const { data, isLoading, refetch } = actionsBeneficiaries.getBeneficiaries({ contractAccessId: merchantAccountId });
    // -- Update beneficiary
    const submitUpdateBeneficiary = actionsBeneficiaries.updateBeneficiary(() => { }, () => { });


    // Functions
    const onUpdateBeneficiary = ({
        beneficiaryUuid,
        payload,
        successEntity,
        errorEntity
    }) => {
        // Submit update beneficiary
        submitUpdateBeneficiary.mutate(
            {
                contractAccessId: merchantAccountId,
                beneficiaryUuid,
                ...payload
            },
            {
                onSuccess: () => {
                    refetch();
                    toast({
                        description: (
                            <Entity entity={successEntity} namespace={nsBeneficiaries} />
                        ),
                        variant: 'success'
                    });
                    setActiveModal(null);
                    setColorPickerBeneficiaryUuid(null);
                },
                onError: () => {
                    toast({
                        description: (
                            <Entity entity={errorEntity} namespace={nsBeneficiaries} />
                        ),
                        variant: 'danger'
                    });
                    setActiveModal(null);
                    setColorPickerBeneficiaryUuid(null);
                }
            }
        );
    };

    return (
        <>
            <div className='flex mb-4'>
                <Button
                    variant='primary'
                    icon={ICONS.ADD}
                    entity='actions.page.add_beneficiary'
                    namespace={nsBeneficiaries}
                    onClick={() => setShowModalCreateBeneficiary(true)}
                    className='h-8 rounded-lg pl-1 pr-2'
                />
            </div>
            <div className='max-h-[calc(100vh-104px)] overflow-y-auto'>
                {isLoading && <Spinner className='full-screen-loading' />}

                {!isLoading && !data?.items?.length && (
                    <div className='flex items-center justify-center py-12 text-muted-foreground text-sm italic'>
                        <Entity entity='actions.beneficiary.no_beneficiaries' namespace={nsBeneficiaries} />
                    </div>
                )}

                {!isLoading && !!data?.items?.length && (
                    <BeneficiaryAccordion
                        beneficiaries={data?.items}
                        openItems={openItems}
                        setOpenItems={setOpenItems}
                        colorPickerBeneficiaryUuid={colorPickerBeneficiaryUuid}
                        setColorPickerBeneficiaryUuid={setColorPickerBeneficiaryUuid}
                        setActiveModal={setActiveModal}
                        onUpdateBeneficiary={onUpdateBeneficiary}
                    />
                )}
            </div>

            {
                showModalCreateBeneficiary &&
                <ModalCreateBeneficiary
                    isOpen={showModalCreateBeneficiary}
                    setIsOpen={setShowModalCreateBeneficiary}
                    merchantAccountId={merchantAccountId}
                    beneficiaries={data?.items ?? []}
                    setLaunchRefetch={() => refetch()}
                />
            }

            {activeModal?.type === BENEFICIARY_ACTION_TYPES.ASSOCIATE_CONTRACT && (
                <ModalAssociateContracts
                    isOpen
                    setIsOpen={() => setActiveModal(null)}
                    merchantAccountId={merchantAccountId}
                    beneficiary={activeModal?.beneficiary}
                    isSubmitting={submitUpdateBeneficiary.isLoading}
                    onSubmit={(contractsProfileUuids) =>
                        onUpdateBeneficiary({
                            beneficiaryUuid: activeModal?.beneficiary?.id,
                            payload: { contractsProfileUuids },
                            successEntity: 'modal.associate_contract.success',
                            errorEntity: 'modal.associate_contract.error'
                        })
                    }
                />
            )}
            {activeModal?.type === BENEFICIARY_ACTION_TYPES.UPDATE_LABEL && (
                <ModalUpdateBeneficiaryLabel
                    isOpen
                    setIsOpen={() => setActiveModal(null)}
                    beneficiary={activeModal?.beneficiary}
                    isSubmitting={submitUpdateBeneficiary.isLoading}
                    onSubmit={(name) =>
                        onUpdateBeneficiary({
                            beneficiaryUuid: activeModal?.beneficiary?.id,
                            payload: { name },
                            successEntity: 'modal.update_label.success',
                            errorEntity: 'modal.update_label.error'
                        })
                    }
                />
            )}
            {
                activeModal?.type === BENEFICIARY_ACTION_TYPES.DELETE_BENEFICIARY &&
                <ModalDeleteBeneficiary
                    isOpen={activeModal?.type === BENEFICIARY_ACTION_TYPES.DELETE_BENEFICIARY}
                    setIsOpen={() => setActiveModal(null)}
                    merchantAccountId={merchantAccountId}
                    beneficiaryUuid={activeModal?.beneficiary?.id}
                    color={activeModal?.beneficiary?.color}
                    name={activeModal?.beneficiary?.name}
                    contracts={activeModal?.beneficiary?.contracts}
                    setLaunchRefetch={() => refetch()}
                />
            }

        </>
    );
}

PayluddBeneficiaries.propTypes = {
    merchantAccountId: PropTypes.string.isRequired
};

export default PayluddBeneficiaries;
